// components/SalesReceivables.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import Table from '../../shared/components/layout/Table';
import Button from '../../shared/components/ui/Button';
import PaginationComponent from '../../shared/components/PaginationComponent';
import NoDataFound from '../components/layout/NoDataFound';

// <-- The APIs
import { getReceivablesReports } from '../services/api/implementation/impl';

// <-- The same utility used in UsersList
import { transformData } from '../../super-admin/utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import Papa from 'papaparse';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import AiSectionBanner from '../components/ai/AiSectionBanner';

const SalesReceivables = () => {
  const location = useLocation();
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  /*
   * ----------------------------------------
   * Data Format Array for the Receivables Table
   * ----------------------------------------
   */
  const receivablesDataFormat = [
    'id',
    'block',
    'unit',
    'customer',
    'plotArea',
    'totalPayable',
    // 'receivableAmount',
    'paid',
    'pending'
  ];

  /*
   * ----------------------------------------
   * Component State
   * ----------------------------------------
   */
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('Company');
  const applyFilterRef = useRef(null);
  const [checkedItems, setCheckedItems] = useState([]);

  // Server-based pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page

  const [selectedProjectId, setSelectedProjectId] = useState(location.state?.projectID || null);

  // Downloading State
  const [isDownloading, setIsDownloading] = useState(false);
  const [dotCount, setDotCount] = useState(0);

  const { register } = useForm();
  const effectiveProjectId = selectedProjectId || location.state?.projectID || null;

  /*
   * ----------------------------------------
   * Fetch Projects
   * ----------------------------------------
   */

  /*
   * ----------------------------------------
   * Fetch Receivables with Server Pagination
   * ----------------------------------------
   */
  const {
    data: receivables, // This object has { data: [...], meta: {...} }
    isFetching: isFetchingReceivables
  } = useQuery({
    // Include page & limit in the query key so that React Query re-fetches
    // whenever page or limit changes
    queryKey: ['receivables', page, limit, effectiveProjectId],
    // Pass page & limit to your function. This is the crucial part.
    queryFn: () => getReceivablesReports(page, limit, effectiveProjectId),
    refetchOnMount: 'always',
    keepPreviousData: true
  });

  // The list of projects
  const projectList = projects || [];

  // The actual Receivables array from your API.
  // e.g.: { status: 200, message: "Order List...", data: [...], meta: {...} }
  const allReceivables = receivables?.data || [];

  // Compute total pages
  const totalItems = receivables?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  /*
   * ----------------------------------------
   * Transform Receivables Data for the Table
   * ----------------------------------------
   */
  const processedReceivables = allReceivables.map((item) => {
    // Calculate paid amount as (totalPayable - due_amount)
    const paidAmount = (item?.totalPayable || 0) - parseFloat(item?.due_amount || 0);

    return {
      // Unique identifier
      id: item?.unit?.id ?? item?.id ?? 'no-id',

      block: item?.blockName || 'N/A',
      unit: item?.unitName || 'N/A',
      customer: item?.leadName || 'N/A',
      plotArea: item?.accommodation?.plotArea
        ? `${item.accommodation.plotArea} ${item.accommodation.measurementUnit || ''}`.trim()
        : 'N/A',

      totalPayable: `₹${(item?.totalPayable || 0).toLocaleString()}`,
      // receivableAmount: `₹${(item?.receivableAmount || 0).toLocaleString()}`, // Commented out as per original format
      paid: `₹${paidAmount.toLocaleString()}`,
      pending: `₹${(item?.due_amount || 0).toLocaleString()}`
    };
  });

  // Transform each row so Table sees the same structure
  const transformedReceivablesData = processedReceivables.map((row) =>
    transformData(row, receivablesDataFormat)
  );

  /*
   * ----------------------------------------
   * Event Handlers
   * ----------------------------------------
   */
  const handlePageChange = (newPage) => {
    // This will trigger the query to re-fetch with the new page
    setPage(newPage);
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
    handleClearAll();
  };

  const handleProjectNameChange = (e) => {
    setSelectedProjectId(e.target.value);
  };

  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
  };

  const handleCheckboxChange = (val) => {
    setCheckedItems((prev) =>
      prev.includes(val) ? prev.filter((item) => item !== val) : [...prev, val]
    );
  };

  const handleClearAll = () => {
    setCheckedItems([]);
    setSelectedFilter('Company');
  };

  // Close filter popup when clicked outside

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Receivables Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (applyFilterRef.current && !applyFilterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
        handleClearAll();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /*
   * ----------------------------------------
   * Handle Download CSV using papaparse
   * ----------------------------------------
   */
  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    try {
      // Fetch all data with page=1 and limit=1000
      const response = await getReceivablesReports(1, 1000);
      const targets = response?.data || [];

      if (targets.length === 0) {
        toast.error('No data available to download.');
        return;
      }

      // Define CSV headers
      const headers = [
        'Block',
        'Unit',
        'Customer',
        'Plot Area',
        'Total Payable',
        'Paid',
        'Pending'
      ];

      // Map data to CSV rows
      const rows = targets.map((item) => {
        const paidAmount = (item?.totalPayable || 0) - parseFloat(item?.due_amount || 0);
        return {
          Block: item?.blockName || 'N/A',
          Unit: item?.unitName || 'N/A',
          Customer: item?.leadName || 'N/A',
          'Plot Area': item?.accommodation?.plotArea
            ? `${item.accommodation.plotArea} ${item.accommodation.measurementUnit || ''}`.trim()
            : 'N/A',
          'Total Payable': item?.totalPayable !== undefined ? item.totalPayable : 0,
          Paid: paidAmount !== undefined ? paidAmount : 0,
          Pending: item?.due_amount !== undefined ? parseFloat(item.due_amount) : 0
        };
      });

      // Convert to CSV using papaparse
      const csv = Papa.unparse({
        fields: headers,
        data: rows
      });

      // Create a Blob from the CSV content
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `sales_receivables_reports.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Download initiated successfully!');
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      toast.error(
        downloadError?.message ||
          'Failed to download the file. Please try again.'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  /*
   * ----------------------------------------
   * Dots Animation Effect
   * ----------------------------------------
   */
  useEffect(() => {
    let interval;
    if (isDownloading) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }

    return () => clearInterval(interval);
  }, [isDownloading]);

  const renderDownloadingText = () => {
    return `Downloading${'.'.repeat(dotCount)}`;
  };

  /*
   * ----------------------------------------
   * Rendering
   * ----------------------------------------
   */
  return (
    <div className="z-5 flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-gray-100">
      {/* Filter Widget */}
      {isFilterOpen && (
        <div
          ref={applyFilterRef}
          className="absolute right-[2rem] mt-[14.5rem] w-[33rem] bg-white border rounded-lg shadow-lg z-[1000]">
          <div className="py-3 px-4 border-b flex justify-between items-center">
            <h4 className="font-semibold">Apply Filters</h4>
            <p
              onClick={handleClearAll}
              className="text-blue-500 underline hover:text-blue-700 transition duration-200 cursor-pointer">
              Clear all
            </p>
          </div>
          <div className="flex justify-between items-start">
            {/* Left Pane (Tabs) */}
            <div className="flex flex-col w-[30%]">
              <p
                className={`py-3 px-4 cursor-pointer border-b  ${
                  selectedFilter === 'Company' ? 'bg-[#d0edf1]' : ''
                }`}
                onClick={() => handleFilterClick('Company')}>
                Company
              </p>
              <p
                className={`py-3 px-4 cursor-pointer ${
                  selectedFilter === 'Month' ? 'bg-[#d0edf1]' : ''
                }`}
                onClick={() => handleFilterClick('Month')}>
                Month
              </p>
              <p
                className={`py-3 px-4 cursor-pointer border-t ${
                  selectedFilter === 'Block' ? 'bg-[#d0edf1]' : ''
                }`}
                onClick={() => handleFilterClick('Block')}>
                Block
              </p>
            </div>
            {/* Right Pane (Checkboxes) */}
            <div className="flex flex-col w-[70%] border-l">
              {selectedFilter === 'Company' && (
                <>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(1)}
                      onChange={() => handleCheckboxChange(1)}
                    />
                    Ved Builders
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(2)}
                      onChange={() => handleCheckboxChange(2)}
                    />
                    Nupur Constructions
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(9)}
                      onChange={() => handleCheckboxChange(9)}
                    />
                    LR Constructions
                  </div>
                </>
              )}
              {selectedFilter === 'Month' && (
                <div className="grid grid-cols-3 gap-1">
                  {[
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December'
                  ].map((month, index) => (
                    <div key={index} className="flex items-center gap-2 py-2 px-2 text-[#67676C]">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={checkedItems.includes(month)}
                        onChange={() => handleCheckboxChange(month)}
                      />
                      {month}
                    </div>
                  ))}
                </div>
              )}
              {selectedFilter === 'Block' && (
                <>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(3)}
                      onChange={() => handleCheckboxChange(3)}
                    />
                    Block A
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(4)}
                      onChange={() => handleCheckboxChange(4)}
                    />
                    Block B
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(5)}
                      onChange={() => handleCheckboxChange(5)}
                    />
                    Block C
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Actions (Cancel/Apply) */}
          <div className="flex justify-end gap-4 p-6 border-t">
            <Button displayType="secondary" onClick={toggleFilter}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                /* Implement Apply Filter Logic */
              }}>
              Apply
            </Button>
          </div>
        </div>
      )}

      {/* Receivables Table — flex fills space below tabs; Table uses h-full from parent */}
      <div className="px-6 pt-2">
        <AiSectionBanner projectId={effectiveProjectId} vertical="accounts" surface="receivables" />
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {isFetchingReceivables ? (
          <div className="pt-12 text-center">
            <p>Loading...</p>
          </div>
        ) : receivables?.data && receivables.data.length > 0 ? (
          <div className="mx-6 mb-4 mt-4 flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="relative h-full min-h-0 flex-1 overflow-hidden">
                <Table
                  headers={[
                    'Block',
                    'Unit',
                    'Customer',
                    'Plot Area',
                    'Total Payable',
                    'Paid',
                    'Pending Amount'
                  ]}
                  data={transformedReceivablesData}
                  identifier="id"
                  isViewEnable={false}
                  isDeleteEnable={false}
                  isEditEnable={false}
                  isWrap={true}
                />
              </div>
              <div className="relative flex shrink-0 justify-center py-2">
                <PaginationComponent
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isFetching={isFetchingReceivables}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="pt-[3rem]">
            <NoDataFound disabledBtn screenHeight={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesReceivables;
